import { useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { useAuth } from './lib/auth.tsx';
import type { SeasonContext } from './lib/seasonContext.ts';
import { SeasonSelectProvider, useSeasonSelect } from './lib/seasonSelect.tsx';
import { CurrentSeasonProvider, useCurrentSeason } from './lib/currentSeason.tsx';
import { MeProvider } from './lib/me.tsx';
import { AvatarProvider } from './lib/avatars.tsx';
import { InstallProvider } from './lib/install.tsx';
import { onWorkerNavigate, syncSubscriptionOnBoot } from './lib/push.ts';
import { UnreadProvider } from './lib/unread.tsx';
import { Shell } from './components/Shell.tsx';
import { Login } from './pages/Login.tsx';
import { MoneyBoard } from './pages/MoneyBoard.tsx';
import { Standings } from './pages/Standings.tsx';
import { AwardPage } from './pages/AwardPage.tsx';
import { RecapPage } from './pages/RecapPage.tsx';
import { Commissioner } from './pages/Commissioner.tsx';
import { Profile } from './pages/Profile.tsx';
import { Manager } from './pages/Manager.tsx';
import { Home } from './pages/Home.tsx';
import { DraftResults } from './pages/DraftResults.tsx';
import { History } from './pages/History.tsx';
import { TipSheetPage } from './pages/TipSheet.tsx';

/** Loads the selected season and hands it to every page via router context. */
function SeasonBoundary() {
  const { seasons, selected } = useSeasonSelect();
  const { data, loading, error, ownerName, live } = useCurrentSeason();

  if (seasons === null || (selected && loading)) return <Loading />;
  if (error) return <Problem message={error} />;
  if (!selected || !data) return <Problem message="No season data yet. Run a sync." />;

  const ctx: SeasonContext = {
    stored: data,
    live,
    nameOf: (id) => ownerName.get(id) ?? 'Unknown',
    teamOf: (id) =>
      data.bundle.data.owners.find((o) => o.id === id)?.teamName ?? '',
  };

  return <Outlet context={ctx} />;
}

/**
 * A page that has moved, keeping its query string.
 *
 * `<Navigate to="/foo" />` drops the search, which would turn a notification
 * for a specific post in a specific week into the current week's board.
 */
function Moved({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function Loading() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <p className="animate-pulse font-mono text-sm text-chalk-dim">tallying the book…</p>
    </div>
  );
}

function Problem({ message }: { message: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-6 text-center">
      <div>
        <p className="font-display text-xl font-bold">Hold up.</p>
        <p className="mt-2 text-sm text-chalk-dim">{message}</p>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    children: [
      // History reads only the checked-in HISTORY_SEASONS data, so it sits
      // OUTSIDE the SeasonBoundary — it must stay reachable even when the live
      // season can't load (API down, or nothing synced yet). The money-board
      // links degrade to hidden when the season list is absent.
      { path: '/history', element: <History /> },
      {
        element: <SeasonBoundary />,
        children: [
          { path: '/', element: <Home /> },
          { path: '/standings', element: <Standings /> },
          { path: '/book', element: <MoneyBoard /> },
          { path: '/draft-results', element: <DraftResults /> },
          { path: '/award/:awardId', element: <AwardPage /> },
          // Pages that moved. Every one of these paths is sitting in a push
          // notification on somebody's phone, and those outlive a deploy — so
          // the old address keeps working rather than 404ing months from now.
          // The query string comes along because that is what names the week
          // and the post the notification was about.
          { path: '/scoreboard', element: <Moved to="/" /> },
          { path: '/war-room', element: <Moved to="/draft-results" /> },
          { path: '/points', element: <Moved to="/standings" /> },
          { path: '/clown', element: <Navigate to="/award/firstRoundClown" replace /> },
          { path: '/recap', element: <RecapPage /> },
          { path: '/tip-sheet', element: <TipSheetPage /> },
          { path: '/profile', element: <Profile /> },
          { path: '/manager/:ownerId', element: <Manager /> },
          { path: '/commissioner', element: <Commissioner /> },
        ],
      },
    ],
  },
]);

export function App() {
  const { user, loading, mock } = useAuth();

  // Re-register this device's push subscription once we're actually signed in:
  // browsers drop subscriptions on their own, and the call that refreshes ours
  // needs a token, so it can't run at page load.
  useEffect(() => {
    if (user) void syncSubscriptionOnBoot();
  }, [user]);

  // A tapped notification, with the app already open, arrives here as a
  // request to move: the worker asks rather than reloading, so the page keeps
  // its state and lands on the exact post. The router exists whether or not
  // it's mounted yet, so this is safe from the login screen too.
  useEffect(() => onWorkerNavigate((path) => void router.navigate(path)), []);

  // InstallProvider wraps the auth branches, not just the signed-in app:
  // beforeinstallprompt fires once, early, and a first visit spends its first
  // seconds on the login screen. Mounting it after sign-in would miss the event
  // outright and leave Profile reporting that installing isn't available.
  return (
    <InstallProvider>
      {loading ? <Loading /> : !user && !mock ? <Login /> : <SignedIn />}
    </InstallProvider>
  );
}

function SignedIn() {
  return (
    <MeProvider>
      <UnreadProvider>
        <AvatarProvider>
          <SeasonSelectProvider>
            <CurrentSeasonProvider>
              <RouterProvider router={router} />
            </CurrentSeasonProvider>
          </SeasonSelectProvider>
        </AvatarProvider>
      </UnreadProvider>
    </MeProvider>
  );
}
